import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  const { session } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfileData = async () => {
    if (!session?.user?.id) return;

    if (!data) setLoading(true);
    const userId = session.user.id;

    try {
      // Fetch Profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Fetch Stats
      const { data: statsData } = await supabase
        .from('user_stats')
        .select('*')
        .eq('profile_id', userId)
        .single();

      // Fetch Applications
      const { data: applicationsData } = await supabase
        .from('applications')
        .select('*')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false });

      const { data: experiencesData } = await supabase
        .from('experiences')
        .select('*')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false });

      const { data: jobAlertsData } = await supabase
        .from('job_alerts')
        .select('*')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false });

      const { data: skillGapsData } = await supabase
        .from('skill_gaps')
        .select('*')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false });

      // Calculate stats dynamically
      const apps = applicationsData || [];
      const totalMatch = apps.reduce((sum, app) => sum + (app.match_score || 0), 0);
      const avgMatch = apps.length > 0 ? Math.round(totalMatch / apps.length) : 0;
      const activeApps = apps.filter(app => app.status !== 'Rejected').length;
      const interviews = apps.filter(app => app.status === 'Interviewing' || app.status === 'Offer').length;

      // Assemble data object matching previous structure
      setData({
        user: {
          ...profileData,
          initials: profileData?.name ? profileData.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U',
          skills: profileData?.skills || [],
          education: profileData?.education || [],
          experience: experiencesData || [],
          publications: profileData?.publications || [],
          gpa: profileData?.gpa || '',
          affiliations: profileData?.affiliations || [],
          certifications: profileData?.certifications || [],
          organizations: profileData?.organizations || [],
          github_url: profileData?.github_url || '',
          portfolio_url: profileData?.portfolio_url || '',
          social_links: profileData?.social_links || [],
          aiFeedback: []
        },
        stats: {
          match: `${avgMatch}%`,
          active: activeApps,
          interviews: interviews,
          rank: statsData?.market_rank || 'N/A'
        },
        applications: applicationsData || [],
        jobAlerts: jobAlertsData || [],
        skillGaps: skillGapsData || []
      });
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      fetchProfileData();
    } else {
      setData(null);
      setLoading(false);
    }
  }, [session]);

  const [cachedOptimizationData, setCachedOptimizationData] = useState(null);

  const refreshData = () => {
    fetchProfileData();
  };

  return (
    <ProfileContext.Provider value={{ data, loading, refreshData, cachedOptimizationData, setCachedOptimizationData }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) throw new Error('useProfile must be used within a ProfileProvider');
  return context;
};